import Quill from 'devextreme-quill';
import * as uuid from 'uuid';
import { HtmlEditorType } from './html-editor.model';
import { TemplateLineHeightLookup } from '../administration/template-builder/template-editor-page/models';

export function customizeQuill(config, editorType: HtmlEditorType, selectionFormattingSig) {
  console.log('Background blot', Quill.imports);
  const Block = Quill.import('blots/block');
  const InlineBlot = Quill.import('blots/inline');
  const Embed = Quill.import('blots/embed');
  const BaseLink = Quill.import('formats/link');
  const Parchment = Quill.import('parchment');

  const IdAttribute = new Parchment.Attributor('id', 'id', {
    scope: Parchment.Scope.INLINE,
  });
  const TypeAttribute = new Parchment.Attributor('type', 'data-type', {
    scope: Parchment.Scope.INLINE,
  });
  const NavigateAttribute = new Parchment.Attributor('navigate', 'data-navigate', {
    scope: Parchment.Scope.INLINE,
  });

  const LineHeightStyle = new Parchment.StyleAttributor('lineHeight', 'line-height');

  // Local helper only. Do not Quill.register this: it uses the same CSS property as
  // Quill's inline size attributor (font-size) and would replace it. Custom sizes
  // such as 28px live on spans, and the replacement drops them as soon as a text
  // or heading block is opened again.
  const BlockSizeStyle = new Parchment.StyleAttributor('blockSize', 'font-size', {
    scope: Parchment.Scope.BLOCK,
  });

  class CustomLink extends BaseLink {
    static blotName = 'link';
    static tagName = 'A';

    static create(value) {
      const node = super.create({ href: '#', target: '_blank' });

      // node.setAttribute('style', 'text-decoration:none;');
      node.setAttribute('id', `link-${uuid.v4()}`);

      if (typeof value === 'string') {
        // const hasProtocol = BaseLink.PROTOCOL_WHITELIST?.some(protocol => value?.startsWith(protocol));
        // const href = hasProtocol ? value : `http://${value}`;

        node.setAttribute('href', value);
        return node;
      }

      if (typeof value !== 'object') {
        return node;
      }

      if (value?.type) {
        node.setAttribute('data-type', value?.type);
      }

      if (value?.href) {
        node.setAttribute('href', value?.href);
      }

      if (value?.target) {
        node.setAttribute('target', value?.target);
      }

      if (value?.navigate) {
        node.setAttribute('data-navigate', value?.navigate);
      }

      if (value?.style) {
        node.setAttribute('style', value?.style);
      }

      if (Object.prototype.hasOwnProperty.call(value, 'style') && !value?.style) {
        node.removeAttribute('style');
      }

      if (value?.innerText) {
        node.setAttribute('innerText', value?.innerText);
      }

      if (value?.innerHTML) {
        node.setAttribute('innerHTML', value?.innerHTML);
      }

      return node;
    }

    format(name, value) {
      if (name !== CustomLink.blotName || !value) {
        super.format(name, value);
        return;
      }

      if (typeof value === 'string') {
        this.domNode.setAttribute('href', value);
        return;
      }

      if (typeof value !== 'object') {
        return;
      }

      if (value?.type) {
        this.domNode.setAttribute('data-type', value?.type);
      }

      if (value?.href) {
        this.domNode.setAttribute('href', value?.href);
      }

      if (value?.target) {
        this.domNode.setAttribute('target', value?.target);
      }

      if (value?.navigate) {
        this.domNode.setAttribute('data-navigate', value?.navigate);
      }

      if (value?.style) {
        this.domNode.setAttribute('style', value?.style);
      }

      if (Object.prototype.hasOwnProperty.call(value, 'style') && !value?.style) {
        this.domNode.removeAttribute('style');
      }
    }

    static formats(domNode) {
      const data = {
        id: domNode.getAttribute('id'),
        href: domNode.getAttribute('href'),
        target: domNode.getAttribute('target'),
        type: domNode.getAttribute('data-type'),
        navigate: domNode.getAttribute('data-navigate'),
        style: domNode.getAttribute('style'),
      };
      return data;
    }
  }

  class Inline extends InlineBlot {
    static tagName = 'SPAN';

    build() {
      super.build();
    }

    optimize(context) {
      super.optimize(context);

      if (!this.prev && !this.next && !this.domNode?.hasAttribute('data-styles-container')) {
        this.domNode?.setAttribute('data-styles-container', true);
      }

      if ((this.prev || this.next) && this.domNode?.hasAttribute('data-styles-container')) {
        this.domNode?.removeAttribute('data-styles-container');
      }
    }
  }

  class StyledBlock extends Block {
    static blotName = 'block';
    static tagName = 'P';

    static create(value) {
      const node = super.create(value);

      node.style.margin = '0';
      node.style.padding = '0';

      return node;
    }

    formats() {
      const blockFormats = super.formats();
      const blockFontSize = BlockSizeStyle.value(this.domNode);

      if (blockFontSize) {
        blockFormats.size = blockFontSize;
      }

      return blockFormats;
    }

    optimize(context) {
      super.optimize(context);

      if (this.children?.length !== 1) {
        return;
      }

      if (this.children?.tail?.domNode?.tagName !== 'BR') {
        return;
      }

      if (!this.prev) {
        return;
      }

      const blockStyles = getBlockStyles(this?.prev?.domNode);
      const formatting = selectionFormattingSig();

      if (!LineHeightStyle.value(this.domNode)) {
        LineHeightStyle.add(this.domNode, blockStyles?.lineHeight);
      }
      if (formatting?.size && !BlockSizeStyle.value(this.domNode)) {
        BlockSizeStyle.add(this.domNode, formatting?.size);
      }
    }
  }

  class HeaderBlock extends Block {
    static blotName = 'block';
    static tagName = 'P'; //['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];

    static create(value: any) {
      const node = super.create(value);
      // node.setAttribute('style', 'margin:0;padding:0;');
      node.style.margin = '0';
      node.style.padding = '0';

      if (value === true || !value || value === '') {
        const selectionFormatting = selectionFormattingSig();
        const heading = selectionFormatting?.heading;
        const h = new Set([1, 2, 3, 4, 5, 6]).has(heading) ? `H${heading}` : 'H1';
        const header = document.createElement(h);
        for (let i = 0; i < node.attributes.length; i++) {
          const attr = node.attributes[i];
          header.setAttribute(attr.name, attr.value);
        }
        return header;
      }

      return node;
    }

    optimize(context) {
      super.optimize(context);
    }
  }

  class SoftBreakBlot extends Embed {
    static blotName = 'softbreak';
    static tagName = 'br';
    static className = 'test';

    optimize() {}
  }
  Quill.register(SoftBreakBlot, true);
  Quill.register(IdAttribute, true);
  Quill.register(TypeAttribute, true);
  Quill.register(NavigateAttribute, true);
  Quill.register(Inline, true);
  Quill.register(LineHeightStyle, true);

  (!editorType || editorType === HtmlEditorType.paragraph) && Quill.register(StyledBlock, true);
  editorType === HtmlEditorType.heading && Quill.register(HeaderBlock, true);
  Quill.register(CustomLink, true);
}

export function getCurrentLineHeightForRange(quill, range) {
  if (!range) {
    return null;
  }

  const [leaf] = quill.getLeaf(range.index);
  if (!leaf) {
    return null;
  }

  const block = quill.scroll.domNode.querySelector(`*[data-index='${range.index}']`) || leaf.domNode.parentElement;

  if (!block) {
    return null;
  }

  const blockStyles = getBlockStyles(block);

  return blockStyles?.lineHeight;
}

export function getBlockStyles(block) {
  const computedStyle = window.getComputedStyle(block);
  const lineHeightPx = parseFloat(computedStyle.lineHeight);
  const fontSizePx = parseFloat(computedStyle.fontSize);

  if (!lineHeightPx || !fontSizePx) {
    return null;
  }

  const lineHeightMultiplier = lineHeightPx / fontSizePx;

  let lineHeight = TemplateLineHeightLookup[0].value;
  let minDiff = Math.abs(lineHeightMultiplier - parseFloat(lineHeight));

  for (const item of TemplateLineHeightLookup) {
    const diff = Math.abs(lineHeightMultiplier - parseFloat(item.value));
    if (diff < minDiff) {
      minDiff = diff;
      lineHeight = item.value;
    }
  }

  return { lineHeight, fontSizePx };
}
